import { Currency } from "@redotech/money/currencies";
import { useHandler } from "@redotech/react-util/hook";
import { StyleShadowRoot } from "@redotech/react-util/style";
import { CartProductType } from "@redotech/redo-model/cart-product-types";
import { defaultToggleTextOptions } from "@redotech/redo-model/default-values";
import { AttachmentStrategy } from "@redotech/redo-model/returns/attachment-strategy";
import { ToggleFields } from "@redotech/redo-model/team";

import {
  CoverageInfo,
  WidgetAutocheck,
  WidgetConfig,
  WidgetFees,
} from "@redotech/redo-model/widget";

import { ModalRootContext } from "@redotech/redo-web/modal-root";
import { setLocalStorageWithExpiry } from "@redotech/redo-web/utils/local-storage-wrapper";
import { memo, ReactNode } from "react";
import { RedoToggleElement } from "../cart-app";
import { CartCard } from "../cart-card";
import { log } from "../monitoring/log";
import { getClassicWidgetTheme } from "../render-elements/classic-widget-theme";
import STYLE_MANAGER from "../style-manager";
import { DeluxeToggleArray, RedoToggle } from "../toggle";
import { RedoProductToSell } from "../utils";
import { SINGLE_TOGGLE_STATE_KEY } from "../utils/signals";
import { getToggleTextOptions } from "./helpers";

function getToggleText(
  autocheck: WidgetAutocheck,
  fees: WidgetFees,
  coverageInfo: CoverageInfo,
) {
  let toggleText = autocheck.checkboxText;

  const defaults = defaultToggleTextOptions(autocheck.coverage);
  const hasProtectionReturnsAndSplit =
    autocheck.packageProtection.coverageEnabled &&
    (autocheck.coverageEnabled ||
      autocheck.finalSaleReturns?.coverageEnabled) &&
    autocheck.packageProtection.splitProducts;

  const allToggleOptions = getToggleTextOptions(autocheck);

  let singleToggleFields: ToggleFields | undefined =
    allToggleOptions.returnToggle;
  switch (fees.mainType) {
    case CartProductType.Return: {
      singleToggleFields = allToggleOptions.returnToggle;

      break;
    }
    case CartProductType.Both: {
      singleToggleFields = allToggleOptions.bothProductToggle;

      break;
    }
    case CartProductType.PackageProtection: {
      singleToggleFields = allToggleOptions.packageProtectionToggle;

      break;
    }
    // No default
  }

  if (hasProtectionReturnsAndSplit) {
    if (
      coverageInfo?.returns.cartEligible &&
      coverageInfo?.packageProtection.cartEligible
    ) {
      singleToggleFields = allToggleOptions.bothProductToggle;
    } else if (coverageInfo?.packageProtection.cartEligible) {
      singleToggleFields = allToggleOptions.packageProtectionToggle;
    } else {
      singleToggleFields = allToggleOptions.packageProtectionToggle;
    }
  }

  if (
    !autocheck.checkboxText &&
    !(
      singleToggleFields.titleText === defaults.bothProductToggle.titleText ||
      singleToggleFields.titleText === defaults.returnToggle.titleText ||
      singleToggleFields.titleText ===
        defaults.packageProtectionToggle.titleText
    )
  ) {
    toggleText = singleToggleFields.titleText;
  }

  if (fees.splitProducts || autocheck.packageProtection.coverageEnabled) {
    toggleText = singleToggleFields.titleText;
  }

  return { toggleText, singleToggleFields };
}

export const SingleToggleManager = memo(function SingleToggleManager({
  autocheck,
  fees,
  toggles,
  redoProductToSell,
  coverageInfo,
  cartCurrency,
  setSingleToggleVal,
  singleToggleState,
}: {
  autocheck: WidgetAutocheck;
  fees: WidgetFees;
  toggles: {
    redoToggleElements: RedoToggleElement[];
    widgetConfig?: WidgetConfig;
  }[];
  redoProductToSell: RedoProductToSell;
  coverageInfo: CoverageInfo;
  cartCurrency: Currency;
  setSingleToggleVal: (value: boolean) => void;
  singleToggleState: boolean;
}) {
  const onToggleChange = useHandler(
    (value: boolean, manual: boolean = true) => {
      log("toggle changed", { value, manual });
      setSingleToggleVal(value);
      const state = value ? "enabled" : "disabled";
      setLocalStorageWithExpiry(SINGLE_TOGGLE_STATE_KEY, state);
    },
  );

  const classicWidgetTheme = getClassicWidgetTheme({ autocheck });
  let singleProductPrice;
  if (
    autocheck.attachmentStrategy ===
    AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE
  ) {
    singleProductPrice = redoProductToSell.rawCalculatedCoveragePrice ?? 0;
  } else {
    singleProductPrice = redoProductToSell.price;
  }

  const { toggleText, singleToggleFields } = getToggleText(
    autocheck,
    fees,
    coverageInfo,
  );

  let cartReactElement: ReactNode | null = null;
  const cartReactElements: ReactNode[] = [];
  if (
    autocheck.attachmentStrategy === AttachmentStrategy.SINGLE_TOGGLE ||
    autocheck.attachmentStrategy ===
      AttachmentStrategy.TOGGLE_CUSTOM_CHECKOUT_FEE ||
    (!autocheck.attachmentStrategy && autocheck.cartToggle.enabled === true)
  ) {
    const deluxeVar = autocheck.toggleSubtextEnabled ?? false;
    const toggle = (
      <RedoToggle
        coverage={autocheck.coverage}
        isDeluxe={deluxeVar}
        onChange={onToggleChange}
        packageProtectionEnabled={autocheck.packageProtection.coverageEnabled}
        packageProtectionPlusEnabled={
          autocheck.packageProtection.packageProtectionPlusEnabled
        }
        paymentModel={autocheck.paidModel}
        price={{
          amount: singleProductPrice || +fees.price,
          currency: cartCurrency,
        }}
        theme={classicWidgetTheme}
        toggleFields={singleToggleFields}
        toggleText={toggleText}
        value={singleToggleState}
      />
    );

    if (deluxeVar) {
      cartReactElement = (
        <DeluxeToggleArray theme={classicWidgetTheme}>
          {toggle}
        </DeluxeToggleArray>
      );
    } else {
      cartReactElement = toggle;
    }
  } else if (autocheck.attachmentStrategy === AttachmentStrategy.CART_CARD) {
    cartReactElement = (
      <CartCard
        coverage={autocheck.coverage}
        onChange={onToggleChange}
        packageProtectionEnabled={autocheck.packageProtection.coverageEnabled}
        packageProtectionPlusEnabled={
          autocheck.packageProtection.packageProtectionPlusEnabled
        }
        price={{
          amount: singleProductPrice || +fees.price,
          currency: cartCurrency,
        }}
        theme={classicWidgetTheme}
        toggleFields={singleToggleFields}
        value={singleToggleState}
      />
    );
  }

  for (const toggle of toggles) {
    cartReactElements.push(
      ...toggle.redoToggleElements.map((toggle, i) => (
        <StyleShadowRoot
          key={`${toggle.id}-${cartReactElements.length + i}`}
          shadowRoot={toggle.root}
          styleManager={STYLE_MANAGER}
        >
          <ModalRootContext.Provider value={toggle.modalContainer}>
            <meta found-selector={toggle.getAttribute("found-selector")} />
            {cartReactElement}
          </ModalRootContext.Provider>
        </StyleShadowRoot>
      )),
    );
  }
  return <>{cartReactElements}</>;
});
